import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  createResource,
  getAllResources,
  updateResource,
  deleteResource,
  getResource,
} from "../../firebase";
import { Resource } from "../../types";
import { format } from "date-fns";
import RichTextEditor from "../../components/RichTextEditor";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CloudArrowUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

// Format date helper
const formatDate = (timestamp: number) => {
  return format(new Date(timestamp), "MMM d, yyyy h:mm a");
};

const ResourceManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tabValue, setTabValue] = useState(0);
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingResources, setLoadingResources] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentResourceId, setCurrentResourceId] = useState<string | null>(
    null
  );

  // Form states
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [published, setPublished] = useState(false);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState<string | null>(null);

  // Notification state
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: "success" | "error";
  }>({
    show: false,
    message: "",
    type: "success",
  });

  // Load resources on component mount
  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      setLoadingResources(true);
      const result = await getAllResources();
      setResources(result);
    } catch (error: any) {
      console.error("Error loading resources:", error);
      showNotification(`Failed to load resources: ${error.message}`, "error");
    } finally {
      setLoadingResources(false);
    }
  };

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setImage(file);

      // Create a preview URL for the selected image
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setTitle("");
    setBody("");
    setImage(null);
    setImagePreview(null);
    setPublished(false);
    setEditMode(false);
    setCurrentResourceId(null);
  };

  const showNotification = (message: string, type: "success" | "error") => {
    setNotification({
      show: true,
      message,
      type,
    });

    // Auto hide after 5 seconds
    setTimeout(() => {
      setNotification((prev) => ({ ...prev, show: false }));
    }, 5000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      showNotification("Please enter a title", "error");
      return;
    }

    try {
      setLoading(true);

      if (editMode && currentResourceId) {
        // Update existing resource
        await updateResource(
          currentResourceId,
          {
            title: title.trim(),
            body,
            published,
          },
          image
        );

        showNotification("Resource updated successfully!", "success");
      } else {
        // Create new resource
        await createResource(
          title.trim(),
          body,
          image,
          currentUser?.uid || "unknown",
          published
        );

        showNotification("Resource created successfully!", "success");
      }

      // Reset form and refresh resources
      resetForm();
      fetchResources();

      // Switch to resources list tab
      setTabValue(1);
    } catch (error: any) {
      console.error("Error saving resource:", error);
      showNotification(`Failed to save resource: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditResource = async (resourceId: string) => {
    try {
      setLoading(true);
      const resource = await getResource(resourceId);

      // Populate form with resource data
      setTitle(resource.title);
      setBody(resource.body);
      setImagePreview(resource.imageURL || null);
      setPublished(resource.published);
      setEditMode(true);
      setCurrentResourceId(resourceId);

      // Switch to edit tab
      setTabValue(0);
    } catch (error: any) {
      console.error("Error loading resource for edit:", error);
      showNotification(`Failed to load resource: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (resourceId: string) => {
    setResourceToDelete(resourceId);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setResourceToDelete(null);
  };

  const handleDeleteResource = async () => {
    if (!resourceToDelete) return;

    try {
      setLoading(true);
      await deleteResource(resourceToDelete);

      // Update local state to reflect deletion
      setResources((prevResources) =>
        prevResources.filter((r) => r.id !== resourceToDelete)
      );

      showNotification("Resource deleted successfully", "success");
    } catch (error: any) {
      console.error("Error deleting resource:", error);
      showNotification(`Failed to delete resource: ${error.message}`, "error");
    } finally {
      setLoading(false);
      closeDeleteDialog();
    }
  };

  return (
    <div className="bg-white rounded-lg">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          <button
            className={`py-4 px-6 font-medium text-sm border-b-2 ${
              tabValue === 0
                ? "border-gold text-gold"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
            onClick={() => handleTabChange(0)}
          >
            {editMode ? "Edit Resource" : "Create Resource"}
          </button>
          <button
            className={`py-4 px-6 font-medium text-sm border-b-2 ${
              tabValue === 1
                ? "border-gold text-gold"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
            onClick={() => handleTabChange(1)}
          >
            Resource List
          </button>
        </nav>
      </div>

      {/* Create/Edit Resource Tab */}
      <div className={`p-6 ${tabValue !== 0 ? "hidden" : ""}`}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {editMode ? "Edit Resource" : "Create New Resource"}
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-12">
              {/* Title */}
              <div className="mb-4">
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Resource Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold"
                  placeholder="Enter title"
                  required
                  disabled={loading}
                />
              </div>

              {/* Image Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Featured Image
                </label>
                <div
                  className="w-full h-64 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer relative overflow-hidden mb-2"
                  onClick={handleImageClick}
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Resource preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <CloudArrowUpIcon className="h-10 w-10 text-gray-400" />
                      <span className="text-gray-500">
                        Click to upload image
                      </span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">
                  Recommended image size: 1200x628 pixels (16:9 ratio)
                </p>
              </div>

              {/* Rich Text Editor */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content
                </label>
                <RichTextEditor
                  value={body}
                  onChange={setBody}
                  placeholder="Enter content here..."
                  readOnly={loading}
                />
              </div>

              {/* Publish Switch */}
              <div className="flex items-center mt-4">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={published}
                    onChange={(e) => setPublished(e.target.checked)}
                    disabled={loading}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  <span className="ms-3 text-sm font-medium text-gray-700">
                    Publish Resource
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Submit/Cancel Buttons */}
          <div className="mt-6 flex justify-end">
            {editMode && (
              <button
                type="button"
                className="mr-4 px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
                onClick={resetForm}
                disabled={loading}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className={`px-4 py-2 bg-gold text-white rounded-md hover:bg-gold-dark ${
                loading ? "cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : editMode
                ? "Update Resource"
                : "Create Resource"}
            </button>
          </div>
        </form>
      </div>

      {/* Resource List Tab */}
      <div className={`p-6 ${tabValue !== 1 ? "hidden" : ""}`}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Resources</h2>
          <button
            className="px-4 py-2 bg-gold text-white rounded-md hover:bg-gold-dark"
            onClick={() => {
              resetForm();
              setTabValue(0);
            }}
          >
            New Resource
          </button>
        </div>

        {loadingResources ? (
          <div className="flex justify-center items-center h-64">
            <div className="border-4 border-t-4 border-gray-300 rounded-full animate-spin w-12 h-12"></div>
          </div>
        ) : resources.length === 0 ? (
          <div className="bg-white p-4 rounded-lg shadow-md">
            <p className="text-gray-500">No resources found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {resources.map((resource) => (
                  <tr key={resource.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {resource.imageURL && (
                          <img
                            src={resource.imageURL}
                            alt={resource.title}
                            className="h-10 w-10 rounded-full object-cover mr-4"
                          />
                        )}
                        <div className="text-sm font-medium text-gray-900">
                          {resource.title}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {formatDate(resource.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          resource.published
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {resource.published ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-4">
                        <button
                          className="bg-white p-1.5 rounded-full text-gray-400 hover:text-gray-500"
                          onClick={() => handleEditResource(resource.id)}
                          disabled={loading}
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          className="bg-white p-1.5 rounded-full text-red-500 hover:text-red-600"
                          onClick={() => openDeleteDialog(resource.id)}
                          disabled={loading}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <div
        className={`fixed inset-0 flex items-center justify-center z-50 ${
          !deleteDialogOpen ? "hidden" : ""
        }`}
      >
        <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        <div className="relative bg-white p-6 rounded-lg shadow-lg w-full max-w-md z-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Confirm Delete
            </h2>
            <button
              className="text-gray-500 hover:text-gray-700"
              onClick={closeDeleteDialog}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <p className="text-gray-700 mb-6">
            Are you sure you want to delete this resource? This action cannot be
            undone.
          </p>
          <div className="flex justify-end">
            <button
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
              onClick={closeDeleteDialog}
            >
              Cancel
            </button>
            <button
              className="ml-4 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              onClick={handleDeleteResource}
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification.show && (
        <div
          className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg ${
            notification.type === "success"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default ResourceManagement;
